/**
 * CARD COPY + FORMATTING (B8). Pure functions only, so they can be unit-tested
 * in Node. Every visible string follows docs/design-spec.md §F (binding):
 * calm, specific, no exclamation marks, "off-peak" always carried by the minutes.
 *
 * Numbers (§B.1): km 1 dp below 100 ("6.4"), integer from 100 ("132").
 * Minutes: integer below 60 ("48 min"); from 60 "1 h 25 min" (figure "1 h 25").
 * Overrides (kmOverride / minutesOverride) win everywhere (displayTravel).
 */
import type { CategoryKind, LngLat, NameSpan, Place } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import { compass, haversine } from "@/lib/geo";

/* ── numbers ────────────────────────────────────────────────────────── */

/** "6.4", "0.3", "132". Never "-0.0" or "NaN". */
export function fmtKm(km: number): string {
  if (!Number.isFinite(km) || km <= 0) return "0.0";
  const r = Math.round(km * 10) / 10;
  return r < 100 ? r.toFixed(1) : String(Math.round(km));
}

/** Whole minutes split into hours and minutes. */
export function minParts(min: number): { h: number; m: number } {
  const t = Number.isFinite(min) && min > 0 ? Math.round(min) : 0;
  return { h: Math.floor(t / 60), m: t % 60 };
}

/** Display figure + unit for a minutes value: 48 → {48, min}; 85 → {"1 h 25", min}; 120 → {"2", h}. */
export function minFig(min: number): { value: string; unit: "min" | "h" } {
  const { h, m } = minParts(min);
  if (h === 0) return { value: String(m), unit: "min" };
  if (m === 0) return { value: String(h), unit: "h" };
  return { value: `${h} h ${m}`, unit: "min" };
}

/** Running text: "48 min", "1 h 25 min", "2 h". */
export function fmtMin(min: number): string {
  const f = minFig(min);
  return `${f.value} ${f.unit}`;
}

/** Drive countdown (§E.6, drive.left): km 1 dp, minutes rounded up. */
export function driveLeft(kmLeft: number, minutesLeft: number): string {
  const km = Math.max(0, kmLeft);
  const min = Math.max(0, Math.ceil(minutesLeft - 1e-6));
  const kmS = km > 0 && km < 0.05 ? "0.1" : fmtKm(km);
  return `${kmS} km · ${fmtMin(min)} to go`;
}

/* ── direction ──────────────────────────────────────────────────────── */

/** Compass word for a bearing from the site: "North-east". */
export const directionWord = (bearing: number): string => compass(bearing);

/** The arrowUpRight icon points north-east at rest, so it turns by bearing − 45°. */
export function arrowRotation(bearing: number): number {
  const b = ((bearing % 360) + 360) % 360;
  return Math.round((b - 45) * 10) / 10;
}

/* ── names and places ───────────────────────────────────────────────── */

const norm = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[–—-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * The place's `via` roads without an entry that is the place itself: a road
 * place never reads "Via Nagar Road" for Pune–Nagar Road ("Pune–Solapur Road"
 * ends with "Solapur Road"). Duplicates collapse.
 */
export function viaRoads(via: readonly string[] | undefined, placeName: string): string[] {
  if (!via) return [];
  const own = norm(placeName);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of via) {
    const k = norm(v);
    if (!k || seen.has(k)) continue;
    if (k === own || own.endsWith(` ${k}`) || k.endsWith(` ${own}`)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

/** card.via: "Via A" · "Via A and B" · "Via A, B and C". null when empty. */
export function viaLine(roads: readonly string[]): string | null {
  if (!roads.length) return null;
  if (roads.length === 1) return `Via ${roads[0]}`;
  return `Via ${roads.slice(0, -1).join(", ")} and ${roads[roads.length - 1]}`;
}

/** card.numbers / card.numbers.override. */
export function numbersNote(p: Pick<Place, "route">): string {
  const r = p.route;
  return r && (r.kmOverride != null || r.minutesOverride != null)
    ? "Off-peak drive time, as advised by Indulkar."
    : "Off-peak drive time by road, from the site's access road.";
}

/** Locality line (§C.4). Road places read "{ref} · nearest access" (§F row.road). */
export function localityLine(p: Pick<Place, "locality" | "crowKm">, kind: CategoryKind | null, roadRef?: string): string | null {
  const loc = p.locality?.trim();
  if (kind === "road") {
    const head = roadRef ? `${roadRef} · nearest access` : "Nearest access";
    return loc ? `${head}, ${loc}` : head;
  }
  if (!loc) return null;
  // Places beyond the Pune region (Navi Mumbai) keep their own locality only.
  if (loc.toLowerCase() === "pune" || p.crowKm >= 60) return loc;
  return `${loc}, Pune`;
}

/** Extra pins note (§C.9, S5 copy). null when there are none. */
export function extraPinsNote(pins: ReadonlyArray<{ future: boolean; note: string }>): string | null {
  if (!pins.length) return null;
  const future = pins.filter((p) => p.future);
  let s: string;
  if (future.length === pins.length) {
    s =
      future.length === 1
        ? "One proposed connection point to the Inner Ring Road is marked on the map."
        : `${future.length} proposed connection points to the Inner Ring Road are marked on the map.`;
  } else {
    s = pins.length === 1 ? "One more point is marked on the map." : `${pins.length} more points are marked on the map.`;
  }
  if (pins.some((p) => /may still change/i.test(p.note))) s += " This point may still change.";
  return s;
}

/** Status words shown in copy (§F status.*); null for existing places. */
export function statusWord(status: string): string | null {
  return ({ upcoming: "Upcoming", proposed: "Proposed", "under-construction": "Under construction", indicative: "Indicative" } as Record<string, string>)[status] ?? null;
}

/** Google Maps link: the client's own link, else a coordinate search. */
export function gmapsHref(p: Pick<Place, "gmapsUrl" | "coord">): string {
  if (p.gmapsUrl) return p.gmapsUrl;
  const [lng, lat] = p.coord;
  return `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/** Deep link to a place (?place=<id>) on the current page. */
export function shareUrl(id: string, loc: { origin: string; pathname: string }): string {
  return `${loc.origin}${loc.pathname}?place=${encodeURIComponent(id)}`;
}

/** share.title / share.text. */
export function shareText(p: Pick<Place, "name" | "route">): { title: string; text: string } {
  const t = displayTravel(p);
  return { title: `${p.name}, from Ládera City`, text: t ? `${fmtMin(t.minutes)} by road, off-peak.` : "" };
}

/* ── future corridors (§C.10) ───────────────────────────────────────── */

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2026-09-24" → "September 2026". Unparseable input comes back as it is. */
export function fmtMonthYear(iso: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const mi = Number(m[2]) - 1;
  return mi >= 0 && mi < 12 ? `${MONTHS[mi]} ${m[1]}` : iso;
}

/**
 * Plain-language source per corridor (the same documents the About sheet names). The data's
 * `sourceDate` is the day the data was prepared, not the document's date, so no date is printed.
 */
const FUTURE_NOTES: Readonly<Record<string, string>> = {
  "inner-ring-road": "Schematic alignment, not the surveyed PMRDA route.",
  "lohegaon-corridors": "Indicative alignment from the Ládera City master plan.",
  "irr-orr-connection": "Indicative alignment from the PMC draft development plan, then along existing roads.",
};
const SUBJECT = "Subject to approvals and change.";

/** card.future.note: where the line comes from, in plain words, without a document date. */
export function futureNote(source: string, corridorId?: string): string {
  const known = corridorId ? FUTURE_NOTES[corridorId] : undefined;
  if (known) return `${known} ${SUBJECT}`;
  const first = source.split(/;|\s[—–]\s/)[0].replace(/\s*\([^)]*\)/g, "").replace(/\s+/g, " ").trim();
  if (/^schematic\b/i.test(first)) return `Schematic alignment. ${SUBJECT}`;
  return `Indicative alignment from ${first || "published references"}. ${SUBJECT}`;
}

/**
 * The future card's first fact (§C.10, amended by QA L3-1): the plan status over "Indicative
 * alignment". The corridors are schematic or indicative lines, so they carry no planned length.
 */
export function futureStatusFact(status: string): { value: string; label: string } | null {
  const word = statusWord(status);
  return word ? { value: word, label: "Indicative alignment" } : null;
}

type LineGeom = { type: "LineString"; coordinates: LngLat[] } | { type: "MultiLineString"; coordinates: LngLat[][] };

/** Drawn length of a corridor geometry in km (not shown: the lines are indicative, §C.10). */
export function corridorKm(g: LineGeom): number {
  const lines = g.type === "LineString" ? [g.coordinates] : g.coordinates;
  let m = 0;
  for (const l of lines) for (let i = 1; i < l.length; i++) m += haversine(l[i - 1], l[i]);
  return m / 1000;
}

/* ── route strip + turn list ────────────────────────────────────────── */

/** Road names in the strip are short: "Lohegaon–Wagholi Rd" (CSS sets the caps). */
export function stripName(name: string): string {
  return name.replace(/\bRoad\b/g, "Rd");
}

export interface StripSegment {
  from: number;
  to: number;
  name: string;
  /** The road's ref ("NH753F"): the short form when the name does not fit. */
  ref?: string;
  /** wide enough to carry a name (and a tick) */
  named: boolean;
  /** The text shown under the segment (estimated layout, see layoutStripLabels), or null. */
  label: string | null;
}

/** A segment carries its name when it spans at least this share of the strip (≈ 40 px of 284). */
export const STRIP_NAME_MIN = 0.14;
/** The strip's track at desktop 1440 (card 392 − margins 16 − whorl 36 − pin 72), in CSS px. */
export const STRIP_TRACK_PX = 268;
/** 9.5/600 caps at .12em tracking: about 7.4 px a character (the estimate when nothing can measure). */
const STRIP_CHAR_PX = 7.4;
/** The strip's label size (§C.1: 9.5/600 caps .12em). */
export const STRIP_FONT_PX = 9.5;

/** Width estimate of a strip label in CSS px (caps, .12em tracking) at 9.5 px. */
export const estimateStripText = (text: string): number => text.length * STRIP_CHAR_PX;

/**
 * Word-boundary cuts of a name, longest first, with an ellipsis ("Sant Dnyaneshwar…", then none shorter
 * than five letters: "Old…" or "Pune…" says nothing about the road). A name is never cut inside a word,
 * so the strip never shows stubs like "DIAM…" (the via line and turn list carry every name in full).
 */
export function stripCuts(name: string): string[] {
  const words = name.split(/(?<=[\s–-])/);
  const out: string[] = [];
  for (let n = words.length - 1; n >= 1; n--) {
    const head = words.slice(0, n).join("").replace(/[\s–-]+$/, "");
    if (head.length >= 5) out.push(`${head}…`);
  }
  return out;
}

/**
 * The label for a segment `px` wide on its own (no neighbours): the whole name when it fits; else the
 * longest word-boundary cut that fits; else null. layoutStripLabels() does the real layout.
 */
export function stripLabel(name: string, px: number, measure: (t: string) => number = estimateStripText): string | null {
  if (measure(name) <= px) return name;
  return stripCuts(name).find((c) => measure(c) <= px) ?? null;
}

/** A placed strip label: its text and box in track px (x from the track's start). */
export interface StripLabel {
  text: string;
  x: number;
  w: number;
  /** The text is a word-boundary cut (the ellipsis of §C.1): only when neither the name nor its ref fits. */
  cut: boolean;
}

export interface StripLayout {
  /** Track width in CSS px. */
  trackPx: number;
  /** Rendered width of a label in CSS px (caps and tracking included). */
  measure: (text: string) => number;
  /** Clear space between two labels (px). */
  gap?: number;
  /** How far a label may run past the track's start and end, under the Whorl and the pin (px). */
  overhang?: readonly [number, number];
}

/** Label spacing at 9.5 px; the kiosk scales it with the type (k = 1.2). */
export const STRIP_GAP_PX = 12;
export const STRIP_OVERHANG_PX: readonly [number, number] = [20, 16];

interface Placed { i: number; a: number; b: number; w: number; text: string; cut: boolean; x: number }

/**
 * Left-to-right placement of labels that must not overlap (gap apart), each centred within its own
 * segment [a, b] and inside the track plus its overhangs. Returns the x positions, nearest to each
 * segment's centre, or null when the set cannot be placed.
 */
function placeLabels(items: Placed[], trackPx: number, gap: number, over: readonly [number, number]): Placed[] | null {
  const xs = [...items].sort((p, q) => p.a - q.a);
  const lo = (p: Placed) => Math.max(p.a - p.w / 2, -over[0]);
  const hi = (p: Placed) => Math.min(p.b - p.w / 2, trackPx + over[1] - p.w);
  let end = Number.NEGATIVE_INFINITY;
  for (const p of xs) {
    p.x = Math.max(lo(p), end + gap);
    if (p.x > hi(p) + 1e-6) return null;
    end = p.x + p.w;
  }
  // pull each label back toward its segment's centre without touching the next one
  for (let k = xs.length - 1; k >= 0; k--) {
    const p = xs[k];
    const next = xs[k + 1];
    const limit = next ? next.x - gap - p.w : hi(p);
    p.x = Math.max(p.x, Math.min((p.a + p.b) / 2 - p.w / 2, hi(p), limit));
  }
  return xs;
}

/**
 * §C.1 labels for the strip's named segments. The longest roads choose first. Each label tries, in order,
 * the name ("Lohegaon–Wagholi Rd"), its ref ("NH753F"), then word-boundary cuts with an ellipsis; the first
 * text that still leaves every label already placed in its segment wins. A label may use the unused room
 * of its neighbours; it keeps its centre inside its own segment. A segment whose name has no room stays
 * unnamed (its tick still marks it).
 */
export function layoutStripLabels(segs: readonly StripSegment[], o: StripLayout): (StripLabel | null)[] {
  const gap = o.gap ?? STRIP_GAP_PX;
  const over = o.overhang ?? STRIP_OVERHANG_PX;
  const order = segs
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.named)
    .sort((p, q) => q.s.to - q.s.from - (p.s.to - p.s.from) || p.i - q.i);
  let placed: Placed[] = [];
  for (const { s, i } of order) {
    const full = [s.name, ...(s.ref && s.ref !== s.name ? [s.ref] : [])];
    const tries = [...full.map((t) => ({ t, cut: false })), ...stripCuts(s.name).map((t) => ({ t, cut: true }))];
    for (const { t, cut } of tries) {
      const item: Placed = { i, a: s.from * o.trackPx, b: s.to * o.trackPx, w: o.measure(t), text: t, cut, x: 0 };
      const next = placeLabels([...placed.map((p) => ({ ...p })), item], o.trackPx, gap, over);
      if (next) {
        placed = next;
        break;
      }
    }
  }
  const out: (StripLabel | null)[] = segs.map(() => null);
  for (const p of placed) out[p.i] = { text: p.text, x: Math.round(p.x * 10) / 10, w: Math.round(p.w * 10) / 10, cut: p.cut };
  return out;
}

/** Consecutive spans with the same name merge. */
export function mergeSpans(spans: readonly NameSpan[]): NameSpan[] {
  const out: NameSpan[] = [];
  for (const s of spans) {
    const last = out[out.length - 1];
    if (last && last.name === s.name && s.fromM - last.toM < 50) last.toM = Math.max(last.toM, s.toM);
    else out.push({ ...s });
  }
  return out;
}

/**
 * Proportional strip segments (0..1) for named spans of at least `minM` metres (§C.1: 1 km). Labels come
 * from layoutStripLabels() with the character estimate on a `trackPx` track; the card lays them out again
 * with the rendered widths (RouteStrip).
 */
export function stripSegments(spans: readonly NameSpan[], lengthM: number, minM = 1000, trackPx = STRIP_TRACK_PX): StripSegment[] {
  if (!(lengthM > 0)) return [];
  const segs: StripSegment[] = mergeSpans(spans)
    .filter((s) => s.toM - s.fromM >= minM)
    .map((s) => {
      const from = Math.max(0, s.fromM / lengthM);
      const to = Math.min(1, s.toM / lengthM);
      return { from, to, name: stripName(s.name), ...(s.ref ? { ref: s.ref } : null), named: to - from >= STRIP_NAME_MIN, label: null };
    });
  const labels = layoutStripLabels(segs, { trackPx, measure: estimateStripText });
  return segs.map((s, i) => ({ ...s, label: labels[i]?.text ?? null }));
}

/**
 * Tick positions (0..1) at the boundaries of the named segments, deduplicated,
 * without the two ends. Unnamed stretches blend into the line, so a 130 km
 * route reads as a few roads, not a fence.
 */
export function stripTicks(segs: readonly StripSegment[]): number[] {
  const t = new Set<number>();
  for (const s of segs) {
    if (!s.named) continue;
    if (s.from > 0.005) t.add(Math.round(s.from * 1000) / 1000);
    if (s.to < 0.995) t.add(Math.round(s.to * 1000) / 1000);
  }
  // a short unnamed gap between two roads would draw a double tick: keep one
  const out: number[] = [];
  for (const x of [...t].sort((a, b) => a - b)) if (!out.length || x - out[out.length - 1] >= 0.015) out.push(x);
  return out;
}

export interface TurnRow { name: string; km: string }

/** The turn list's row for stretches the data does not name (§E.6: the list accounts for the whole route). */
export const LOCAL_ROADS = "Local roads";
/** Named stretches shorter than this read as part of the local roads around them. */
const TURN_MIN_M = 50;

const turnName = (s: NameSpan): string => (s.ref && !s.name.includes(s.ref) ? `${s.name} (${s.ref})` : s.name);

/**
 * Tenths of a km per row that add up to the route's own total (largest remainder), each at least 0.1,
 * so the km column always sums to the total line (within its rounding).
 */
function tenths(lengths: readonly number[], totalM: number): number[] {
  const raw = lengths.map((m) => m / 100);
  const out = raw.map((u) => Math.max(1, Math.floor(u)));
  const target = Math.max(out.length, Math.round(totalM / 100));
  let diff = target - out.reduce((a, b) => a + b, 0);
  const byRemainder = raw.map((u, i) => ({ i, r: u - Math.floor(u), floor: Math.floor(u) >= 1 })).sort((p, q) => q.r - p.r || p.i - q.i);
  for (let k = 0; diff > 0 && byRemainder.length; k = (k + 1) % byRemainder.length) {
    out[byRemainder[k].i] += 1;
    diff -= 1;
  }
  for (let k = byRemainder.length - 1, guard = 0; diff < 0 && guard < 4 * out.length; k = (k - 1 + byRemainder.length) % byRemainder.length, guard++) {
    const i = byRemainder[k].i;
    if (out[i] > 1) {
      out[i] -= 1;
      diff += 1;
    }
  }
  return out;
}

/**
 * "The route, road by road": one row per named stretch, in order. With the route's length the rows cover
 * the whole route: the stretches the data leaves unnamed (before the first road, between two roads, after
 * the last one, and named bits under 50 m) read as "Local roads", adjacent ones merged, and the km column
 * adds up to the total. Without it, only the named stretches are listed.
 */
export function turnRows(spans: readonly NameSpan[], lengthM?: number): TurnRow[] {
  const merged = mergeSpans(spans);
  if (!(lengthM && lengthM > 0)) {
    return merged.filter((s) => s.toM - s.fromM >= TURN_MIN_M).map((s) => ({ name: turnName(s), km: fmtKm(Math.max(0.1, (s.toM - s.fromM) / 1000)) }));
  }
  const rows: { name: string; m: number; local: boolean }[] = [];
  const push = (name: string, m: number, local: boolean) => {
    if (!(m > 0)) return;
    const last = rows[rows.length - 1];
    if (local && last?.local) last.m += m;
    else rows.push({ name, m, local });
  };
  let at = 0;
  for (const s of merged) {
    const from = Math.min(Math.max(s.fromM, at), lengthM);
    const to = Math.min(Math.max(s.toM, from), lengthM);
    push(LOCAL_ROADS, from - at, true);
    if (to - from >= TURN_MIN_M) push(turnName(s), to - from, false);
    else push(LOCAL_ROADS, to - from, true);
    at = Math.max(at, to);
  }
  push(LOCAL_ROADS, lengthM - at, true);
  // a local stretch too short to list joins the road before it (or after it, at the start)
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r.local || r.m >= TURN_MIN_M || rows.length === 1) continue;
    const into = rows[i - 1] ?? rows[i + 1];
    into.m += r.m;
    rows.splice(i, 1);
    i -= 1;
  }
  const t = tenths(
    rows.map((r) => r.m),
    lengthM,
  );
  return rows.map((r, i) => ({ name: r.name, km: fmtKm(t[i] / 10) }));
}

/* ── bloom summaries (§F) ───────────────────────────────────────────── */

const BLOOM_NOUNS: Record<string, readonly [string, string]> = {
  "arterial-roads": ["arterial road", "arterial roads"],
  "future-infrastructure": ["planned corridor", "planned corridors"],
  "logistic-hubs": ["airport or station", "airports and stations"],
  education: ["school or college", "schools and colleges"],
  healthcare: ["hospital", "hospitals"],
  "lifestyle-retail": ["place to shop", "places to shop"],
  "urban-centres": ["urban centre", "urban centres"],
  hospitality: ["place to stay", "places to stay"],
  "business-parks": ["business park", "business parks"],
  industrial: ["industrial belt", "industrial belts"],
  "premium-benchmarks": ["premium benchmark", "premium benchmarks"],
  "township-benchmarks": ["township", "townships"],
  "neighbouring-benchmarks": ["neighbouring project", "neighbouring projects"],
  "upcoming-townships": ["upcoming township", "upcoming townships"],
};

/**
 * The noun for {n} places of a category: "1 school or college", "22 schools and colleges".
 * `upcoming`: every counted place is upcoming, so the noun says so ("2 upcoming places to stay"),
 * and a summary never implies somewhere that exists today (QA L3-11).
 */
export function bloomNoun(categoryId: string, n: number, fallbackLabel = "places", upcoming = false): string {
  const pair = BLOOM_NOUNS[categoryId];
  const noun = !pair ? (n === 1 ? "place" : fallbackLabel.toLowerCase()) : n === 1 ? pair[0] : pair[1];
  return upcoming && !/^upcoming\b/.test(noun) ? `upcoming ${noun}` : noun;
}

/** Whether every counted place of a category is upcoming or proposed (the site never counts). */
export function bloomAllUpcoming(places: readonly Pick<Place, "category" | "isSite" | "status">[], categoryId: string): boolean {
  let n = 0;
  for (const p of places) {
    if (p.category !== categoryId || p.isSite) continue;
    if (p.status === "existing") return false;
    n++;
  }
  return n > 0 && categoryId !== "future-infrastructure";
}

/** Whether the summary carries "nearest {min} min" (future corridors have no drive time). */
export const bloomHasNearest = (categoryId: string): boolean => categoryId !== "future-infrastructure";

/** "22 schools and colleges · nearest 6 min off-peak" / "3 planned corridors" (off-peak: QA M2, L3-10). */
export function bloomSummary(categoryId: string, n: number, nearestMin: number | null, upcoming = false): string {
  const head = `${n} ${bloomNoun(categoryId, n, "places", upcoming)}`;
  return bloomHasNearest(categoryId) && nearestMin != null ? `${head} · nearest ${fmtMin(nearestMin)} off-peak` : head;
}

/** Nearest display minutes over a category's places (the site is never counted). */
export function nearestMinutes(places: readonly Place[], categoryId: string): number | null {
  let best: number | null = null;
  for (const p of places) {
    if (p.category !== categoryId || p.isSite) continue;
    const t = displayTravel(p);
    if (t && (best == null || t.minutes < best)) best = t.minutes;
  }
  return best;
}

/* ── notes, peek line, totals ───────────────────────────────────────── */

/**
 * §C.8 note: `place.notes`, then `route.note`. A note that only repeats the
 * status the card already shows ("Upcoming") is dropped, as is a repeat.
 */
export function noteLines(p: Pick<Place, "notes" | "route" | "status">): string[] {
  const out: string[] = [];
  const status = statusWord(p.status)?.toLowerCase();
  for (const raw of [p.notes, p.route?.note]) {
    let t = raw?.trim().replace(/\s+/g, " ");
    // a leading "Proposed." repeats the status tag above the name
    if (t && status && t.toLowerCase().startsWith(`${status}.`)) t = t.slice(status.length + 1).trim();
    if (!t) continue;
    const bare = t.replace(/[.\s]+$/, "").toLowerCase();
    if (bare === status || out.some((o) => o.replace(/[.\s]+$/, "").toLowerCase() === bare)) continue;
    out.push(/[.!?]$/.test(t) ? t : `${t}.`);
  }
  return out;
}

/** Mobile peek line (§A.4): "11.4 km · 18 min". null without drive numbers. */
export function peekLine(p: Pick<Place, "route">): string | null {
  const t = displayTravel(p);
  return t ? `${fmtKm(t.km)} km · ${fmtMin(t.minutes)}` : null;
}

/** Total line of the turn list: "9.8 km · 13 min off-peak". */
export function totalLine(p: Pick<Place, "route">): string | null {
  const t = displayTravel(p);
  return t ? `${fmtKm(t.km)} km · ${fmtMin(t.minutes)} off-peak` : null;
}

/** Drive HUD arrival meta: "9.8 km · 13 min off-peak" (same as the total). */
export const arrivalMeta = totalLine;
